import { useCallback, useRef } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { Button } from '@/shared/ui/primitives'

type FeedbackAttachmentPickerProps = {
  previewUrl: string | null
  fileName: string | null
  disabled?: boolean
  onSelect: (file: File | null) => void
  onClear: () => void
}

export function FeedbackAttachmentPicker({
  previewUrl,
  fileName,
  disabled,
  onSelect,
  onClear,
}: FeedbackAttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null
      onSelect(file)
      if (inputRef.current) inputRef.current.value = ''
    },
    [onSelect],
  )

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium">Screenshot (optional)</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label="Attach a screenshot"
        disabled={disabled}
        onChange={handleChange}
      />
      {previewUrl ? (
        <div className="flex items-center gap-3 rounded-lg border bg-card p-2">
          <img
            src={previewUrl}
            alt="Attachment preview"
            className="size-14 rounded-md border object-cover"
          />
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{fileName}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove attachment"
            disabled={disabled}
            onClick={onClear}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="w-full bg-card"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus className="size-4" />
          Add screenshot
        </Button>
      )}
    </div>
  )
}
